<template>
    <ExtendedLayout
        :project="project"
        currentTab="general"
    >

        <Panel>

            <form @submit.prevent="submit">

                <h2 class="text-lg font-bold mb-2">
                    {{ __('admin::projects.master_data') }}
                </h2>

                <FormGroup :label="__('admin::labels.name')" :error="errors.name">
                    <Input name="name" type="text" :has-error="exists(errors.name)" v-model="form.name" :required="true" />
                </FormGroup>

                <FormGroup :label="__('admin::labels.department')" :error="errors.department_id">
                    <Select name="department_id" :options="departmentOptions" :has-error="exists(errors.department_id)" v-model="form.department_id" :required="true" :allow-empty="false" />
                </FormGroup>

                <FormGroup :label="__('admin::labels.type')" :error="errors.type">
                    <Select name="type" :options="typeOptions" :has-error="exists(errors.type)" v-model="form.type" :required="true" :allow-empty="false" />
                </FormGroup>

                <FormGroup :label="__('admin::labels.status')" :error="errors.status">
                    <Select name="status" :options="statusOptions" :has-error="exists(errors.status)" v-model="form.status" :required="true" :allow-empty="false" />
                </FormGroup>

                <FormGroup :label="__('admin::labels.case_material')" :error="errors.case_material">
                    <FileLinkInput v-model="form.case_material" />
                </FormGroup>

                <FormGroup :label="__('admin::labels.access_key')" :error="errors.access_key">
                    <div class="relative">
                        <Input name="access_key" type="text" :has-error="exists(errors.access_key)" v-model="form.access_key" :required="true" :allow-empty="false" />
                        <button type="button" class="absolute top-0 right-0 rounded-r-md bg-gray-100 p-3 border border-gray-300" @click="generateAccessKey">
                            <RefreshIcon class="w-4 h-4" />
                        </button>
                    </div>
                </FormGroup>

                <FormGroup class="space-y-2">
                    <Toggle name="is_active" v-model:checked="form.is_active" :label="__('admin::labels.is_active')" />
                    <Toggle name="show_on_united_site" v-model:checked="form.show_on_united_site" :label="__('admin::labels.show_on_united_site')" />
                    <Toggle name="in_active_operation" v-model:checked="form.in_active_operation" :label="__('admin::labels.in_active_operation')" />
                    <Toggle name="delete_manually_created_posts" v-model:checked="form.delete_manually_created_posts" :label="__('admin::labels.delete_manually_created_posts')" />
                </FormGroup>

                <h2 class="text-lg font-bold mb-2 mt-2">
                    {{ __('admin::projects.location') }}
                </h2>

                <FormGroup :label="__('admin::labels.address')">
                    <Input name="address" type="text" v-model="form.address" :required="false" />
                </FormGroup>

                <div class="grid grid-cols-3 gap-4">
                    <FormGroup :label="__('admin::labels.zipcode')" :error="errors.zipcode">
                        <Input name="zipcode" type="text" :has-error="exists(errors.zipcode)" v-model="form.zipcode" :required="false" />
                    </FormGroup>

                    <FormGroup :label="__('admin::labels.city')" :error="errors.city">
                        <Input name="city" type="text" :has-error="exists(errors.city)" v-model="form.city" :required="false" />
                    </FormGroup>

                    <FormGroup :label="__('admin::labels.country')" :error="errors.country">
                        <Select name="country" :options="countryOptions" :has-error="exists(errors.country)" v-model="form.country" :required="false" />
                    </FormGroup>
                </div>

                <div class="grid grid-cols-2 gap-4">
                    <FormGroup :label="__('admin::labels.latitude')" :error="errors.latitude">
                        <Input name="latitude" type="number" step="any" :has-error="exists(errors.latitude)" v-model="form.latitude" :required="false" />
                    </FormGroup>

                    <FormGroup :label="__('admin::labels.longitude')" :error="errors.longitude">
                        <Input name="longitude" type="number" step="any" :has-error="exists(errors.longitude)" v-model="form.longitude" :required="false" />
                    </FormGroup>
                </div>

                <h2 class="text-lg font-bold mb-2 mt-2">
                    {{ __('admin::projects.united_site') }}
                </h2>

                <FormGroup :label="__('admin::labels.short_description')" :error="errors.short_description">
                    <Textarea name="short_description" :has-error="exists(errors.short_description)" v-model="form.short_description" :required="false" />
                </FormGroup>

                <FormGroup :label="__('admin::labels.external_site_link')" :error="errors.external_site_link">
                    <Input name="external_site_link" type="text" :has-error="exists(errors.external_site_link)" v-model="form.external_site_link" :required="false" />
                </FormGroup>

                <FormGroup :label="__('admin::labels.featured_image')" :error="errors.featured_image_id">
                    <AdvancedFileInput v-model="form.featured_image_id" />
                </FormGroup>

                <FormGroup :label="__('admin::labels.gallery_images')" :error="errors.gallery_image_ids">
                    <AdvancedFileInput v-model="form.gallery_image_ids" multiple />
                </FormGroup>

                <Button :loading="form.processing" class="mt-2">
                    {{ __('admin::general.update_model', {'model': __('admin::projects.project').toLowerCase() }) }}
                </Button>

            </form>

        </Panel>

        <Panel class="mt-5">
            <div v-if="project.legacy_id">
                <div class="font-bold">V1:</div>
                {{ $route('api::v1.projects.show', { project_id: project.legacy_id, api_key: project.access_key })}}
            </div>

            <div class="font-bold mt-4">V2:</div>
            {{ $route('api::v2.projects.show', { project_id: project.id, api_key: project.access_key })}}

            <div class="flex gap-2 mt-6">
                <Button type="button" @click="triggerFeedGeneration">
                    {{ __('admin::projects.trigger_feed_generation') }}
                </Button>
                <Button type="button" @click="triggerWordPressPush">
                    {{ __('admin::projects.trigger_wordpress_push') }}
                </Button>
            </div>
        </Panel>

    </ExtendedLayout>
</template>

<script>
import ExtendedLayout from '@/Pages/Projects/Edit';
import Panel from "@/Components/Panel";
import Button from "@/Components/Button";
import FormGroup from "@/Components/FormGroup";
import Input from "@/Components/Input";
import Select from "@/Components/Select";
import Toggle from "@/Components/Toggle";
import {RefreshIcon} from "@heroicons/vue/outline";
import { v4 as uuidv4 } from 'uuid';
import Textarea from "@/Components/Textarea";
import AdvancedFileInput from "@/Components/AdvancedFileInput";
import FileLinkInput from "@/Components/FileLinkInput.vue";

export default {
    components: {
        FileLinkInput,
        AdvancedFileInput,
        Textarea,
        Toggle,
        Select,
        Input,
        FormGroup,
        Button,
        Panel,
        ExtendedLayout,
        RefreshIcon,
    },
    props: {
        errors: Object,
        project: Object,
        departmentOptions: Object,
        typeOptions: Object,
        statusOptions: Object,
        countryOptions: Object,
    },
    data() {
        return {
            form: this.$inertia.form({
                name: this.project.name,
                department_id: this.project.department_id,
                type: this.project.type,
                status: this.project.status,
                case_material: this.project.case_material,
                access_key: this.project.access_key,
                is_active: this.project.is_active,
                show_on_united_site: this.project.show_on_united_site,
                in_active_operation: this.project.in_active_operation,
                delete_manually_created_posts: this.project.delete_manually_created_posts,
                address: this.project.address,
                zipcode: this.project.zipcode,
                city: this.project.city,
                country: this.project.country || 'DK',
                latitude: this.project.latitude,
                longitude: this.project.longitude,
                short_description: this.project.short_description,
                external_site_link: this.project.external_site_link,
                featured_image_id: this.project.featured_image_id,
                gallery_image_ids: this.project.gallery_image_ids && Object.values(this.project.gallery_image_ids).length ? this.project.gallery_image_ids : [],
            })
        }
    },
    methods: {
        generateAccessKey() {
            this.form.access_key = uuidv4();
        },
        submit() {
            this.form
                .transform((data) => ({
                    ...data,
                    _method: 'patch',
                }))
                .post(this.$route('admin::projects.general.update', { project: this.project.id }));
        },
        triggerFeedGeneration() {
            this
                .$inertia
                .post(this.$route('admin::projects.trigger-feed-generation', { project: this.project.id }))
        },
        triggerWordPressPush() {
            this
                .$inertia
                .post(this.$route('admin::projects.trigger-wordpress-push', { project: this.project.id }))
        },
    },
}
</script>
